import { ChangeEvent, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import {
  RangeSettings,
  RangeSettingsInput,
  RangeSettingsPostfix,
} from './range-field.style';
import { RangeSlider } from 'lib/form/range/range';
import ValidationMessage from 'approot/shared/validation/validation-message';
import { validateNumberInput } from 'approot/shared/logic/number-input-validation';

export const RangeFieldSetting = ({
  value,
  fieldName,
  label,
  postfix,
  max,
  min,
  onChange,
}: {
  value: number;
  fieldName: string;
  postfix?: string;
  label: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
}) => {
  const {
    formState: { errors },
    setValue,
    register,
    trigger,
  } = useFormContext();

  const handleRangeChange = useCallback(
    async (changeEvent: ChangeEvent<HTMLInputElement>) => {
      const value = Number(changeEvent.target.value);
      setValue(fieldName, value, { shouldValidate: true });
      await trigger(fieldName);

      if (errors[fieldName]) return;
      onChange(value);
    },
    [setValue, fieldName, trigger, errors, onChange]
  );

  const height = value > max ? max : value;

  return (
    <SettingsGroup>
      <SettingsLabel htmlFor={fieldName}>{label}</SettingsLabel>
      <RangeSettings>
        <RangeSlider
          id={`${fieldName}-slider`}
          value={height}
          min={min}
          max={max}
          step={1}
          handleChange={handleRangeChange}
        />
        <RangeSettingsInput
          data-selector="range-setting"
          id={fieldName}
          $width={80}
          {...register(fieldName, {
            validate: value => validateNumberInput({ value, min, max }),
          })}
          onChange={handleRangeChange}
          aria-invalid={!!errors?.[fieldName]}
          aria-describedby={`${fieldName}-error`}
          $hasError={!!errors?.[fieldName]}
        />
        {postfix && <RangeSettingsPostfix>{postfix}</RangeSettingsPostfix>}
      </RangeSettings>
      <ValidationMessage
        id={`${fieldName}-error`}
        field={fieldName}
        errors={errors}
      />
    </SettingsGroup>
  );
};
